import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, Warehouse as WarehouseIcon } from "lucide-react";

const FIELDS = [
  { name: "name", label: "Warehouse name", required: true },
  { name: "code", label: "Code", required: true, placeholder: "WH-EU-01" },
  { name: "country", label: "Country ISO", placeholder: "US, DE, SG…" },
];

export default function Warehouse() {
  const [warehouses, setWarehouses] = useState([]);
  const [stats, setStats] = useState({});
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    Promise.all([api.get("/warehouses"), api.get("/dashboard/by-lifecycle")]).then(([a, b]) => {
      setWarehouses(a.data.items || []);
      const s = {};
      (b.data.items || []).forEach((r) => (s[r.state] = r.count));
      setStats(s);
    });
  };
  useEffect(() => { load(); }, []);

  const archive = async (w) => {
    try { await api.delete(`/warehouses/${w.id}`); toast.success("Warehouse deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Operations</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Warehouse</h1>
          <p className="text-sm text-slate-500 mt-0.5">Central inventory and stock disposition.</p>
        </div>
        <Button className="bg-slate-900" onClick={() => setDialog({ open: true, initial: null })} data-testid="wh-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add warehouse
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["In Warehouse", stats.in_warehouse || 0],
          ["Ready to Deploy", stats.ready_to_deploy || 0],
          ["Reserved", stats.reserved || 0],
          ["Awaiting Inspection", stats.awaiting_inspection || 0],
        ].map(([label, v]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-2xl font-heading font-bold text-slate-900 mt-1 tabular-nums">{v.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 font-heading font-semibold text-slate-900">Warehouses</div>
        <div className="divide-y divide-slate-100">
          {warehouses.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">No warehouses yet.</div>}
          {warehouses.map((w) => (
            <div key={w.id} data-testid={`wh-row-${w.id}`} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
              <div className="flex items-center gap-2.5">
                <WarehouseIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                <div>
                  <div className="text-sm font-medium">{w.name}</div>
                  <div className="text-xs text-slate-500">{w.code} · {w.country}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge value={w.active ? "active" : "inactive"} tone={w.active ? "green" : "slate"} />
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDialog({ open: true, initial: w })} data-testid={`wh-edit-${w.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => setConfirm(w)} data-testid={`wh-archive-${w.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <CrudDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        title={dialog.initial ? `Edit ${dialog.initial.name}` : "Add warehouse"}
        fields={FIELDS}
        initial={dialog.initial}
        endpoint={dialog.initial ? `/warehouses/${dialog.initial.id}` : "/warehouses"}
        method={dialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="wh"
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Delete ${confirm?.name}?`}
        description="Blocked if assets currently sit in this warehouse."
        onConfirm={() => archive(confirm)}
        testId="wh-confirm"
      />
    </div>
  );
}
